// deployment_class: customer_env_allowed
/**
 * RequireAuthGate — TOP-LEVEL closed-system auth gate (2026-06-20).
 *
 * Kathedral.ai runs as a CLOSED system for the demo: a logged-out visitor must
 * see the LOGIN page and NOTHING else (no Marketplace, no data) until they
 * authenticate. This gate wraps the WHOLE routed app and enforces that:
 *
 *   - status 'authenticated'                 → render children (full app)
 *   - status 'idle' / 'loading'              → neutral aria-busy placeholder
 *                                              (does NOT flash any portal)
 *   - status 'unauthenticated' / 'error'     → redirect to /auth/login with a
 *                                              safe returnTo
 *
 * AUTH ROUTES ARE EXEMPT. The `/auth/*` subtree (sign-in, create-account, and
 * CRITICALLY the email-verification landing at `/auth/login?verified=1`) MUST
 * remain reachable to an ANONYMOUS visitor — a prospective user clicks the
 * verify link in their email BEFORE they can log in. App.tsx renders the
 * `/auth/*` routes OUTSIDE this gate; this component is only mounted around the
 * gated (non-auth) subtree, so the exemption is structural — an anonymous user
 * on `/auth/login` never reaches this gate and is never redirected (no loop).
 *
 * This is distinct from `RequireAuth` (per-route guard, still used inside the
 * gated subtree for portals that ALSO need org-membership/scope checks). The
 * gate is the OUTER closed-system boundary; RequireAuth/RequireScope remain the
 * INNER fine-grained guards. Both are intentionally retained.
 */
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { LandingPreview } from '../LandingPreview';

export function RequireAuthGate({ children }: { children: ReactNode }): JSX.Element {
  const { status } = useAuth();
  const location = useLocation();

  // While the session is being resolved, render a neutral loading state — NEVER
  // the Marketplace or any portal (no pre-auth data flash).
  if (status === 'idle' || status === 'loading') {
    return (
      <div
        className="min-h-screen bg-paper p-page text-warm-gray-d text-body-s"
        aria-busy="true"
      >
        Loading…
      </div>
    );
  }

  // Anything that is not a confirmed authenticated session is bounced to login —
  // EXCEPT the site root, which is the PUBLIC marketing landing (kathedral.ai's
  // default page · E3 2026-08-01). An anonymous visitor at `/` sees the landing
  // (no gated data — all illustrative); every OTHER path still redirects to login.
  if (status !== 'authenticated') {
    if (location.pathname === '/') return <LandingPreview />;
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/login?returnTo=${returnTo}`} replace />;
  }

  return <>{children}</>;
}
